import React from 'react';
import { ErrorState as KitErrorState } from '@parsina/framework';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { errorMessageKey } from '../../lib/bffClient.js';

// A failed READ, said in the app's language.
//
// The kit ships an `ErrorState`, and this is a thin wrapper over it for one
// reason: the kit's defaults are Persian STRING LITERALS («در دسترس نیست»،
// «تلاش مجدد»), and this app ships `en` as well. A screen that used the kit's
// component directly would render a Persian heading over an English page —
// which is what happened to the first draft of the patients list.
//
// It also picks the SENTENCE from the error's code (core.md §14): the BFF's own
// message is secondary detail, and the headline the student reads comes from
// the catalogue. `errors.unavailable` for a downstream that did not answer,
// `errors.notFound` for a row that is not theirs, `errors.generic` for anything
// the vocabulary does not name.
//
// ── This is for a READ that failed, never for an empty one ───────────────
//
// «شما هنوز بیماری نساخته‌اید» and «نتوانستیم بپرسیم» are different facts and
// only the first one means "start writing". Every hook in this app keeps
// `error` and "no rows" apart for exactly that reason, and a screen that
// rendered `EmptyState` on a failed query would tell a student their work was
// gone.

export default function ErrorState({
  error,
  title,
  description,
  onRetry,
  fill = false,
  style,
  ...rest
}) {
  const { t } = useTranslation();
  // The BFF's Persian message rides underneath as detail — never as the
  // headline, which has to resolve in both locales.
  const detail = typeof error?.message === 'string' ? error.message : '';
  const headline = title ?? t(errorMessageKey(error));
  return (
    <KitErrorState
      title={headline}
      description={description ?? (detail || t('errors.unavailableBody'))}
      onRetry={onRetry}
      retryLabel={t('common.actions.retry')}
      fill={fill}
      style={style}
      data-testid="error-state"
      {...rest}
    />
  );
}
